import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { DATE_TIME_FORMAT_NAME_MODEL, getDateFormat, setMomentLanguage } from '../../../../Apis/functions';

/**
 * Custom hook/component to manage connection status UI data.
 *
 * <p>
 * Provides localized current time and formatted date string.
 * Handles language synchronization with moment.js and updates
 * the time every second.
 * </p>
 *
 * @author Karen Yunuen Pozos Arias
 */
const ConnectionStatusApp = () => {
    const { i18n } = useTranslation();
    const { t } = useTranslation();

    const [hour, setHour] = useState(moment());

    /**
     * Capitalizes the first letter of a string.
     *
     * @param {string} str input string
     * @returns {string} string with the first letter capitalized
     */
    const capitalizeFirstLetter = (str) =>
        str.charAt(0).toUpperCase() + str.slice(1);

    /**
     * Updates moment.js locale based on the current i18n language.
     *
     * <p>
     * Maps application language codes to moment.js locales.
     * Defaults to English if no match is found.
     * </p>
     */
  useEffect(() => {
    setMomentLanguage(i18n.language);
}, [i18n.language]);

    /**
     * Updates the current time every second.
     *
     * <p>
     * Uses an interval to keep the hour state synchronized
     * with the current system time.
     * </p>
     */
    useEffect(() => {
        const interval = setInterval(() => {
            setHour(moment());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    /**
     * Formats the current date into a localized string.
     *
     * <p>
     * Example format: "Monday 5 March"
     * The first letter is capitalized for display consistency.
     * </p>
     *
     * @type {string}
     */
    const dateFormat = capitalizeFirstLetter(
    getDateFormat(
        new Date(),
        null,
        DATE_TIME_FORMAT_NAME_MODEL.weekDayMonth
    )
);

    return {
        t,
        hour,
        dateFormat
    };
};

export default ConnectionStatusApp;
